import { ReactComponent as SuccessIcon } from '@basqet-dashboard/common/assets/icons/success.svg';
import { Button, Modal, RingLoader } from '@basqet-dashboard/common/components';
import {
  DeclineBanner,
  InfoIcon,
  RestartOnboardingModal,
  StepNavigator,
  Tooltip
} from '@components/onboarding';
import { KYB_ACCOUNT_TYPE } from '@config';
import { useCompliance } from '@hooks/useCompliance';
import { canRestartProfile, useRestartOnboarding } from '@hooks/useRestartOnboarding';
import { getRemoteFileByAttachmentField, mapAttachmentMetaByField } from '@utils/attachment-meta';
import {
  unmapCorporateBusinessDetails,
  unmapDirector,
  unmapDueDiligence,
  unmapPepEntry,
  unmapShareholder,
  unmapUBO
} from '@utils/compliance-mappers';
import {
  buildDeclinedStoreState,
  deriveStepReviewStatuses,
  isStepReadOnly
} from '@utils/section-review-status';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { BusinessDetails } from './corporate-non-fi/steps/business-details';
import { CompanyRegistration } from './corporate-non-fi/steps/company-registration';
import { DueDiligenceQuestionnaire } from './corporate-non-fi/steps/due-diligence-questionnaire';
import { OwnershipStructure } from './corporate-non-fi/steps/ownership-structure';
import { PepDeclaration } from './corporate-non-fi/steps/pep-declaration';

const STEP_TITLES = {
  1: { normal: 'Business', cursive: 'Details' },
  2: { normal: 'Ownership', cursive: 'Structure' },
  3: { normal: 'PEP and Government Official', cursive: 'Declaration' },
  4: { normal: 'Company Registration &', cursive: 'Compliance Docs' },
  5: { normal: 'Due Diligence', cursive: 'Questionnaire' }
};

const getProfileStatus = (profile) => profile?.status ?? profile?.kyc?.status;
const getSectionKey = (section) => section.section_key ?? section.key;

const deriveStoreStateFromProfile = (profile, mappedStepData, totalSteps) => {
  const profileSteps = Array.isArray(profile?.steps) ? profile.steps : [];
  const completedFromApi = profileSteps
    .filter((step) => step.ui_status === 'COMPLETE')
    .map((step) => step.step);

  const completedStepIds =
    completedFromApi.length > 0
      ? [...new Set(completedFromApi)].sort((a, b) => a - b)
      : Object.keys(mappedStepData)
          .map(Number)
          .sort((a, b) => a - b);

  const currentFromApi = profileSteps.find((step) => step.ui_status === 'CURRENT')?.step;
  const firstIncomplete = profileSteps.find((step) => step.ui_status !== 'COMPLETE')?.step;
  const fallbackCurrent =
    completedStepIds.length > 0 ? Math.min(totalSteps, Math.max(...completedStepIds)) : 1;

  return {
    completedStepIds,
    currentStep: currentFromApi ?? firstIncomplete ?? fallbackCurrent,
    showSuccess: getProfileStatus(profile) === 'PENDING' || getProfileStatus(profile) === 'APPROVED'
  };
};

const pepTooltipContent = (
  <>
    <strong className="pep-tooltip__heading">Who is a government official?</strong>
    <span className="pep-tooltip__text">
      A government official is anyone who works for or represents:
    </span>
    <ul className="pep-tooltip__list">
      <li>Any government or government department, agency, or authority at any level</li>
      <li>Any entity owned or controlled (fully or partly) by a government</li>
      <li>Any public international or supranational body (for example, the UN or World Bank)</li>
    </ul>
    <hr className="pep-tooltip__divider" />
    <strong className="pep-tooltip__heading">Who is a Politically Exposed Person (PEP)?</strong>
    <span className="pep-tooltip__text">
      A PEP is someone who holds or has held a prominent public position. Examples include senior
      politicians, senior government or military officials, senior executives of state-owned
      entities, judges, or senior political party officials. Immediate family members and close
      associates are also considered PEPs.
    </span>
  </>
);

const extractPepPersons = (ownershipData) => {
  if (!ownershipData) return [];

  const persons = [];
  (ownershipData.directors || []).forEach((d) => {
    if (d.is_pep === 'yes') persons.push({ name: d.name, type: 'director' });
  });
  (ownershipData.shareholders || []).forEach((s) => {
    if (s.is_pep === 'yes') persons.push({ name: s.name, type: 'shareholder' });
  });
  (ownershipData.ubos || []).forEach((u) => {
    if (u.is_pep === 'yes') persons.push({ name: u.name, type: 'ubo' });
  });
  return persons;
};

/**
 * Shared shell for corporate verification flows (FI and non-FI).
 *
 * @param {object}   props
 * @param {string}   props.className     — BEM block class (e.g. 'corp-fi-verification')
 * @param {Function} props.useStore      — Zustand store hook
 * @param {Array}    props.steps         — step definitions for StepNavigator
 * @param {boolean}  [props.showDDQ]     — whether to render the DDQ step (default true)
 * @param {Function} [props.onComplete]  — optional wrapper around store's completeStep
 * @param {string}   [props.kybAccountType] — KYB_ACCOUNT_TYPE value (default CORPORATE_NON_FI)
 * @returns {React.ReactElement}
 */
const CorporateVerificationBase = ({
  className,
  useStore,
  steps,
  showDDQ = true,
  onComplete,
  kybAccountType = KYB_ACCOUNT_TYPE.CORPORATE_NON_FI
}) => {
  const history = useHistory();
  const currentStep = useStore((s) => s.currentStep);
  const completedStepIds = useStore((s) => s.completedStepIds);
  const stepData = useStore((s) => s.stepData);
  const ownershipData = useStore((s) => s.stepData[2]);
  const showSuccess = useStore((s) => s.showSuccess);
  const pageLoading = useStore((s) => s.pageLoading);
  const stepReviewStatuses = useStore((s) => s.stepReviewStatuses);
  const completeStep = useStore((s) => s.completeStep);
  const navigateToStep = useStore((s) => s.navigateToStep);
  const updateStepData = useStore((s) => s.updateStepData);
  const setPageLoading = useStore((s) => s.setPageLoading);
  const resetFlow = useStore((s) => s.resetFlow);

  const { initializeKyc, loadProfile } = useCompliance();
  const {
    showRestartModal,
    isRestarting,
    setCanRestart,
    handleBackClick,
    handleRestart,
    closeRestartModal
  } = useRestartOnboarding({ resetFlow });

  // Capture steps.length in a ref so it's available inside the init effect
  // without causing re-initialization when the step count changes dynamically
  // (e.g. DNFI detection adds DDQ step after step 1 is saved).
  const stepsLengthRef = useRef(steps.length);
  stepsLengthRef.current = steps.length;

  useEffect(() => {
    const initialize = async () => {
      try {
        const profile = await loadProfile();

        if (profile) {
          setCanRestart(canRestartProfile(profile));
          const profileStatus = getProfileStatus(profile);

          const mappedStepData = {};
          profile.sections.forEach((section) => {
            const sectionKey = getSectionKey(section);

            if (sectionKey === 'BUSINESS_DETAILS' && section.entries.length > 0) {
              const entry = section.entries[0];
              mappedStepData[1] = {
                ...unmapCorporateBusinessDetails(entry.data),
                proof_of_address_attachment_id: entry.data.proof_of_address_attachment_id || null,
                attachment_meta: entry.data.attachment_meta || [],
                _entryId: entry.entry_id,
                _version: entry.version
              };
            }
            if (
              sectionKey === 'OWNERSHIP_DIRECTORS' ||
              sectionKey === 'OWNERSHIP_SHAREHOLDERS' ||
              sectionKey === 'OWNERSHIP_UBOS'
            ) {
              // Combine ownership sections into step 2
              if (!mappedStepData[2])
                mappedStepData[2] = { directors: [], shareholders: [], ubos: [] };
              if (sectionKey === 'OWNERSHIP_DIRECTORS') {
                const dirEntryIds = {};
                mappedStepData[2].directors = section.entries.map((e) => {
                  dirEntryIds[e.entry_id] = e.entry_id;
                  return unmapDirector(e.data, e.entry_id);
                });
                mappedStepData[2]._directorEntryIds = dirEntryIds;
              }
              if (sectionKey === 'OWNERSHIP_SHAREHOLDERS') {
                const shEntryIds = {};
                mappedStepData[2].shareholders = section.entries.map((e) => {
                  shEntryIds[e.entry_id] = e.entry_id;
                  return unmapShareholder(e.data, e.entry_id);
                });
                mappedStepData[2]._shareholderEntryIds = shEntryIds;
              }
              if (sectionKey === 'OWNERSHIP_UBOS') {
                const uboEntryIds = {};
                mappedStepData[2].ubos = section.entries.map((e) => {
                  uboEntryIds[e.entry_id] = e.entry_id;
                  const attachmentMetaByField = mapAttachmentMetaByField(e.data?.attachment_meta);
                  const remoteFile = getRemoteFileByAttachmentField(
                    attachmentMetaByField,
                    'means_of_identification_attachment_id'
                  );
                  return unmapUBO(e.data, e.entry_id, remoteFile);
                });
                mappedStepData[2]._uboEntryIds = uboEntryIds;
              }
            }
            if (sectionKey === 'OWNERSHIP_DOCUMENTS' && section.entries.length > 0) {
              if (!mappedStepData[2]) {
                mappedStepData[2] = { directors: [], shareholders: [], ubos: [] };
              }

              const entry = section.entries[0];
              const attachmentMetaByField = mapAttachmentMetaByField(entry.data?.attachment_meta);

              mappedStepData[2].particulars_attachment_id =
                entry.data.particulars_of_directors_attachment_id || null;
              mappedStepData[2].certified_list_attachment_id =
                entry.data.certified_shareholder_register_attachment_id || null;
              mappedStepData[2].particulars_file = getRemoteFileByAttachmentField(
                attachmentMetaByField,
                'particulars_of_directors_attachment_id'
              );
              mappedStepData[2].certified_list_file = getRemoteFileByAttachmentField(
                attachmentMetaByField,
                'certified_shareholder_register_attachment_id'
              );
            }
            if (sectionKey === 'PEP_DECLARATION' && section.entries.length > 0) {
              // Check if it's a "no PEPs" entry
              const firstEntry = section.entries[0];
              if (firstEntry.data.pep_confirmed_none) {
                mappedStepData[3] = { pep_confirmed_none: true, peps: [], _pepEntryIds: {} };
              } else {
                // Deduplicate by full_name + position_in_org — the backend
                // can accumulate duplicate PEP entries across resubmissions.
                const seen = new Set();
                const uniqueEntries = section.entries.filter((e) => {
                  const key = `${e.data.full_name}|${e.data.position_in_org}`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });

                const pepEntryIds = {};
                mappedStepData[3] = {
                  pep_confirmed_none: false,
                  peps: uniqueEntries.map((e) => {
                    pepEntryIds[e.entry_id] = e.entry_id;
                    return unmapPepEntry(e.data, e.entry_id);
                  }),
                  _pepEntryIds: pepEntryIds
                };
              }
            }
            if (sectionKey === 'COMPLIANCE_DOCS' && section.entries.length > 0) {
              mappedStepData[4] = section.entries[0].data;
            }
            if (sectionKey === 'DUE_DILIGENCE' && section.entries.length > 0) {
              mappedStepData[5] = unmapDueDiligence(section.entries[0].data);
            }
          });

          const derived = deriveStoreStateFromProfile(
            profile,
            mappedStepData,
            stepsLengthRef.current
          );

          if (profileStatus === 'DECLINED') {
            const reviewStatuses = deriveStepReviewStatuses(profile);
            if (Object.keys(reviewStatuses).length > 0) {
              const allStepIds = Array.from({ length: stepsLengthRef.current }, (_, i) => i + 1);
              useStore.setState(
                buildDeclinedStoreState({ allStepIds, reviewStatuses, mappedStepData })
              );
            } else {
              useStore.setState({
                stepData: mappedStepData,
                completedStepIds: derived.completedStepIds,
                currentStep: derived.currentStep,
                showSuccess: derived.showSuccess
              });
            }
          } else {
            useStore.setState({
              stepData: mappedStepData,
              completedStepIds: derived.completedStepIds,
              currentStep: derived.currentStep,
              showSuccess: derived.showSuccess
            });
          }
        } else {
          resetFlow();
          // Initialize new KYC record
          const merchantType =
            kybAccountType === KYB_ACCOUNT_TYPE.CORPORATE_FI ? 'CORP_FI' : 'CORP_NON_FI';
          await initializeKyc(merchantType);
          setCanRestart(true);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize KYC:', error);
      } finally {
        setPageLoading(false);
      }
    };

    initialize();
  }, [loadProfile, initializeKyc, setPageLoading, resetFlow, kybAccountType, useStore]); // steps.length excluded — read from ref to avoid re-initialization when step count changes dynamically

  const pepPersons = useMemo(() => extractPepPersons(ownershipData), [ownershipData]);

  const handleComplete = (step, data) => {
    setCanRestart(true);
    if (onComplete) {
      onComplete(step, data);
      return;
    }
    completeStep(step, data);
  };

  const title = STEP_TITLES[currentStep];

  return (
    <div className={className}>
      <button
        type="button"
        className={`${className}__back`}
        disabled={pageLoading}
        onClick={handleBackClick}>
        &larr; Back
      </button>

      <div
        className={`${className}__progress`}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={currentStep}>
        {steps.map((step) => {
          const isActive = completedStepIds.includes(step.id) || step.id === currentStep;
          return (
            <span
              key={step.id}
              className={`${className}__progress-segment${
                isActive ? ` ${className}__progress-segment--active` : ''
              }`}
            />
          );
        })}
      </div>

      <div className={`${className}__body`}>
        <aside className={`${className}__sidebar`}>
          <StepNavigator
            steps={steps}
            currentStep={currentStep}
            completedStepIds={completedStepIds}
            onStepClick={navigateToStep}
            stepReviewStatuses={stepReviewStatuses}
          />
        </aside>

        <div className={`${className}__content`}>
          {pageLoading ? (
            <div className={`${className}__loader`}>
              <RingLoader />
            </div>
          ) : (
            <>
              <div className={`${className}__content-header`}>
                <div className={`${className}__content-header-row`}>
                  <h2 className={`${className}__content-title`}>
                    {title.normal} <span className="text-cursive">{title.cursive}</span>
                  </h2>
                  {currentStep === 3 ? (
                    <Tooltip
                      content={pepTooltipContent}
                      width={380}
                      openOn="hover"
                      trigger={
                        <button type="button" className={`${className}__pep-hint corp-label`}>
                          Am I a PEP?
                          <InfoIcon />
                        </button>
                      }
                    />
                  ) : (
                    <span className={`${className}__step-count`}>
                      {currentStep} OF {steps.length}
                    </span>
                  )}
                </div>
                {currentStep === 3 && (
                  <p className={`${className}__content-subtitle`}>
                    Provide details for any directors, shareholders, UBOs, or other individuals you
                    have already identified as PEPs. Also indicate if anyone in your company&apos;s
                    management, employees, Board of Directors, subsidiaries, or affiliates is a
                    government official.
                  </p>
                )}
              </div>

              {/* Keep visited steps mounted but hidden so form values persist when navigating back */}
              {(currentStep === 1 || completedStepIds.includes(1)) && (
                <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                  {stepReviewStatuses[1]?.status === 'DECLINED' && currentStep === 1 && (
                    <DeclineBanner reasons={stepReviewStatuses[1].reasons} />
                  )}
                  <BusinessDetails
                    onComplete={(data) => handleComplete(1, data)}
                    initialData={stepData[1]}
                    readOnly={isStepReadOnly(stepReviewStatuses, 1)}
                  />
                </div>
              )}
              {(currentStep === 2 || completedStepIds.includes(2)) && (
                <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
                  {stepReviewStatuses[2]?.status === 'DECLINED' && currentStep === 2 && (
                    <DeclineBanner reasons={stepReviewStatuses[2].reasons} />
                  )}
                  <OwnershipStructure
                    onComplete={(data) => handleComplete(2, data)}
                    initialData={stepData[2]}
                    onSaveDraft={(data) => updateStepData(2, data)}
                    readOnly={isStepReadOnly(stepReviewStatuses, 2)}
                  />
                </div>
              )}
              {(currentStep === 3 || completedStepIds.includes(3)) && (
                <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
                  {stepReviewStatuses[3]?.status === 'DECLINED' && currentStep === 3 && (
                    <DeclineBanner reasons={stepReviewStatuses[3].reasons} />
                  )}
                  <PepDeclaration
                    onComplete={(data) => handleComplete(3, data)}
                    pepPersons={pepPersons}
                    initialData={stepData[3]}
                    onSaveDraft={(data) => updateStepData(3, data)}
                    readOnly={isStepReadOnly(stepReviewStatuses, 3)}
                  />
                </div>
              )}
              {(currentStep === 4 || completedStepIds.includes(4)) && (
                <div style={{ display: currentStep === 4 ? 'block' : 'none' }}>
                  {stepReviewStatuses[4]?.status === 'DECLINED' && currentStep === 4 && (
                    <DeclineBanner reasons={stepReviewStatuses[4].reasons} />
                  )}
                  <CompanyRegistration
                    onComplete={(data) => handleComplete(4, data)}
                    initialData={stepData[4]}
                    kybAccountType={kybAccountType}
                    isLastStep={!showDDQ}
                    industry={stepData[1]?.industry}
                    readOnly={isStepReadOnly(stepReviewStatuses, 4)}
                  />
                </div>
              )}
              {showDDQ && (currentStep === 5 || completedStepIds.includes(5)) && (
                <div style={{ display: currentStep === 5 ? 'block' : 'none' }}>
                  {stepReviewStatuses[5]?.status === 'DECLINED' && currentStep === 5 && (
                    <DeclineBanner reasons={stepReviewStatuses[5].reasons} />
                  )}
                  <DueDiligenceQuestionnaire
                    onComplete={(data) => handleComplete(5, data)}
                    initialData={stepData[5]}
                    onSaveDraft={(data) => updateStepData(5, data)}
                    readOnly={isStepReadOnly(stepReviewStatuses, 5)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showSuccess && (
        <Modal
          Icon={SuccessIcon}
          header={{ name: 'Thanks for submitting your KYB', cursive: 'information' }}
          onModalClose={() => history.push('/')}>
          <p className="modal__text">
            Our team is reviewing your application and you&apos;ll receive an update within 3
            business days. You can continue using Basqet in Test Mode while the review is in
            progress.
          </p>
          <Button isFull onClick={() => history.push('/')}>
            Go to Dashboard
          </Button>
        </Modal>
      )}

      <RestartOnboardingModal
        isOpen={showRestartModal}
        onClose={closeRestartModal}
        onRestart={handleRestart}
        isLoading={isRestarting}
      />
    </div>
  );
};

CorporateVerificationBase.propTypes = {
  className: PropTypes.string.isRequired,
  useStore: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
  showDDQ: PropTypes.bool,
  onComplete: PropTypes.func,
  kybAccountType: PropTypes.oneOf([
    KYB_ACCOUNT_TYPE.CORPORATE_NON_FI,
    KYB_ACCOUNT_TYPE.CORPORATE_FI
  ])
};

export { CorporateVerificationBase };
